import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import { PaymentIntentResult } from '@stripe/stripe-js';
import axios from 'axios';
import { insertBefore } from 'helpers/dom-helper';
import { isEmpty } from 'helpers/metal';
import StripeSetup from 'subscriptions/helpers/stripe_setup';

type CreateSubscriptionResponse = {
  client_secret: string;
  customer_subscription_id: string;
  paid: boolean;
};

@registerElement('subscription-form')
export default class SubscriptionFormElement extends ImpulseElement {
  /**
   * The current id of the organization that is subscribing.
   */
  @property() orgId: string;

  /**
   * The interval to bill the customer.
   */
  @property() billingInterval: 'month' | 'year' = 'month';

  // The HTML form element.
  @target() form: HTMLFormElement;
  @target() referralNameInput: HTMLInputElement;
  // Misc
  @target() submitButton: HTMLButtonElement;
  @target() cancelButton: HTMLAnchorElement;
  @target() alertContainer: HTMLTemplateElement;

  // This is being set by the manage_seat_element.ts
  subscriptionData: Record<string, { totalSeatsCount: number; costPerSeat: number }> = {};
  private stripeSetup: StripeSetup;

  /**
   * Called when the element is connected to the DOM.
   */
  async connected() {
    this.stripeSetup = new StripeSetup(this.orgId);

    try {
      await this.stripeSetup.initialize();
    } catch (error) {
      this.showAlert('An error occurred while loading the subscription form. Please try again.');
    }
  }

  /**
   * Handles the child form submission.
   */
  async handleFormSubmit(event: Event) {
    // We need the subscriptionData.
    if (isEmpty(this.subscriptionData)) return;
    event.preventDefault();

    if (this.stripeSetup.formValid) {
      try {
        this.emit('loadstart');
        this.toggleFormDisability(true);
        this.toggleButtonDisability(true);
        const { data } = await this.createSubscription();
        const stripeResponse = await this.confirmCardPayment(data.client_secret);
        await this.afterConfirmCardPayment(stripeResponse);
      } catch (error) {
        this.showAlert();
      } finally {
        this.toggleFormDisability(false);
        this.toggleButtonDisability(false);
        this.emit('loadend');
      }
    } else {
      this.stripeSetup.checkValidity();
    }
  }

  /**
   * Create the organization's subscription with the quantity of seats that they've chosen.
   */
  private createSubscription() {
    const plan: Record<string, { quantity: number }> = {};
    // eslint-disable-next-line guard-for-in
    for (const productName in this.subscriptionData) {
      plan[productName] ||= { quantity: 0 };
      plan[productName].quantity = this.subscriptionData[productName].totalSeatsCount;
    }

    return axios.post<CreateSubscriptionResponse>('/api/v2/subscriptions', {
      subscription: { interval: this.billingInterval, plan },
    });
  }

  /**
   * Charge customer's card.
   */
  private confirmCardPayment(secret: string) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.stripe!.confirmCardPayment(secret, {
      payment_method: this.stripeSetup.paymentMethodAttrs,
      setup_future_usage: 'off_session', // See https://stripe.com/docs/api/payment_intents/confirm#confirm_payment_intent-setup_future_usage
    });
  }

  private async afterConfirmCardPayment({ paymentIntent, error }: PaymentIntentResult) {
    if (error) {
      // SCA, incomplete payment, insufficient funds, etc.
      window.location.reload();
      return;
    }

    if (this.referralNameInput.value) {
      await this.createCustomerReferral();
    }

    if (paymentIntent?.status === 'succeeded') {
      this.form.submit();
    }
  }

  private createCustomerReferral() {
    return axios.post('/api/v2/customer-referrals', {
      customer_referral: {
        organization_id: this.orgId,
        name: this.referralNameInput.value,
      },
    });
  }

  private toggleFormDisability(value: boolean) {
    this.stripeSetup.toggleFormDisability(value);
    this.referralNameInput.disabled = value;
  }

  private toggleButtonDisability(disabled: boolean) {
    this.submitButton.disabled = disabled;
    this.submitButton.toggleAttribute('loading', disabled);
    this.cancelButton.classList.toggle('disabled', disabled);
  }

  private get stripe() {
    return this.stripeSetup.stripe;
  }

  private showAlert(message = 'An error occurred while processing your subscription. Please try again.') {
    const clone = this.alertContainer.content.cloneNode(true) as HTMLElement;
    const messageBox = clone.querySelector<HTMLElement>('[data-message-box]');
    if (!messageBox) return;

    messageBox.innerText = message;
    insertBefore(clone, this.alertContainer);
  }
}
